import { Dispatch } from "react";
import {
  KYDAdminFeedItem,
  KYDExternalLinkMetadata,
  KYDFeedItem,
} from "@common/interfaces/KYDOrganization";
import { capitalized } from "../../utils";
import {
  Button,
  HStack,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import ExternalEventForm from "./ExternalEventForm";
import NewsForm from "./NewsForm";
import actions from "../../actions";
import S3Upload from "react-s3-uploader/s3upload";
import { useDialog } from "../../Common/Dialog";

const EditLinkModal = ({
  type,
  feedItem,
  dirtyText,
  loading,
  updateDirtyText,
  onClose,
  onCreateLink,
  onUpdateLink,
  setEditingFeedItem,
  fetchMetadata,
  isOpen = false,
  updateLoading,
}: {
  isOpen: boolean;
  type: KYDFeedItem["type"];
  feedItem?: KYDAdminFeedItem;
  dirtyText: Record<string, any>;
  loading: Record<"link" | "tickettype", boolean>;
  onClose: () => void;
  updateDirtyText: Dispatch<any>;
  fetchMetadata: (link: string) => Promise<{
    og: KYDExternalLinkMetadata;
    metadata: KYDExternalLinkMetadata;
  }>;
  onCreateLink: (dt: any, type: KYDFeedItem["type"]) => Promise<void>;
  onUpdateLink: (
    linkId: string,
    update?: {
      values?: any;
      enabled?: boolean;
      should_delete?: boolean;
      order?: string[];
    }
  ) => Promise<void>;
  setEditingFeedItem: Dispatch<KYDAdminFeedItem | null>;
  updateLoading: (id: string, state: boolean) => void;
}) => {
  const isEdit = !!feedItem;

  const dialog = useDialog();

  const uploadFile = async () => {
    const file = dirtyText["item.fileToUpload"];
    if (!file) {
      return null;
    }

    updateLoading("link", true);
    return actions.ADMIN.createUploadUrl(file, async (response) => {
      const signedUrl = response;
      const uploader = new S3Upload({
        getSignedUrl: (_file, callback) => callback(signedUrl),
        accept: "image/png,image/jpg,image/jpeg",
        uploadRequestHeaders: {
          "x-amz-acl": "private",
        },
        onError: (e) => {
          console.log("image error", e);
          dialog({
            title: "Image upload",
            text: `Error uploading image: ${e}. This might be due to insufficient permissions.`,
          });
        },
      });

      try {
        await uploader.uploadFile(file);
        return signedUrl.fileKey;
      } catch (e) {
        console.error("Upload failed with error:", e);
        dialog({
          title: "S3 Upload Error",
          text: `Failed to upload to S3: ${e}. Please contact support.`,
        });
      } finally {
        updateLoading("link", false);
      }
    });
  };

  const onClickCreate = async () => {
    updateLoading("link", true);
    const fileUrl = await uploadFile();

    return onCreateLink(
      {
        ...dirtyText,
        "item.image_override": fileUrl,
      },
      type
    );
  };

  const onClickSave = async () => {
    const fileUrl = await uploadFile();
    return onUpdateLink(feedItem.id, {
      values: {
        ...dirtyText,
        "item.image_override": fileUrl,
      },
    });
  };

  const onLinkChange = async (link: string) => {
    updateDirtyText({ [`item.link`]: link });

    if (link === "") {
      return;
    }

    updateLoading("link.preview", true);

    const { og } = await fetchMetadata(link);

    updateDirtyText({
      "item.image": og?.image,
      "item.title": og?.title,
      "item.image_override": null,
      "item.fileToUpload": null,
    });

    updateLoading("link.preview", false);
  };

  return (
    <Modal
      size={"3xl"}
      isOpen={isOpen}
      onClose={() => onClose()}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`${feedItem ? "Edit " : "Create "}${
          type ? capitalized(type.split("_").shift()) : ""
        } Link`}</ModalHeader>
        <ModalBody>
          <Stack>
            <Stack spacing={0}>
              <Text>Link</Text>
              <InputGroup>
                <Input
                  isDisabled={loading["link"]}
                  placeholder="Link"
                  onChange={(e) => {
                    onLinkChange(e.target.value);
                  }}
                  value={
                    `item.link` in dirtyText
                      ? dirtyText[`item.link`]
                      : isEdit
                        ? feedItem.original_link
                        : ""
                  }
                />
                {loading["link.preview"] ? (
                  <InputRightAddon>
                    <Spinner />
                  </InputRightAddon>
                ) : null}
              </InputGroup>
            </Stack>
            {type === "external_event" ? (
              <ExternalEventForm
                dirtyText={dirtyText}
                feedItem={feedItem}
                loading={loading}
                updateDirtyText={updateDirtyText}
              />
            ) : null}
            {type === "news" ? (
              <NewsForm
                dirtyText={dirtyText}
                label="Header"
                feedItem={feedItem}
                loading={loading}
                updateDirtyText={updateDirtyText}
              />
            ) : null}
            {type === "music" ? (
              <NewsForm
                label="Header"
                dirtyText={dirtyText}
                feedItem={feedItem}
                loading={loading}
                updateDirtyText={updateDirtyText}
              />
            ) : null}
          </Stack>
        </ModalBody>

        <ModalFooter justifyContent={"space-between"}>
          <HStack>
            {isEdit ? (
              <Button
                left={0}
                color="white"
                bg="red.500"
                isLoading={loading[feedItem.id]}
                isDisabled={loading[feedItem.id]}
                onClick={async () => {
                  return onUpdateLink(feedItem.id, {
                    should_delete: true,
                  });
                }}
              >
                Delete
              </Button>
            ) : null}
          </HStack>

          <HStack>
            <Button
              isDisabled={loading[isEdit ? feedItem.id : "link"]}
              onClick={() => {
                updateDirtyText("all");
                onClose();
                setEditingFeedItem(null);
              }}
              mr="4"
            >
              Cancel
            </Button>
            {!isEdit ? (
              <Button
                color="white"
                bg="green.500"
                isDisabled={loading["link"] || loading["link.preview"]}
                isLoading={loading["link"]}
                onClick={() => onClickCreate()}
              >
                Create
              </Button>
            ) : null}
            {isEdit ? (
              <Button
                color="white"
                bg="green.500"
                isLoading={loading["link"] || loading[feedItem.id]}
                isDisabled={
                  loading["link"] ||
                  loading[feedItem.id] ||
                  loading["link.preview"]
                }
                onClick={() => onClickSave()}
              >
                Save
              </Button>
            ) : null}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditLinkModal;
